import React from "react";
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Font,
} from "@react-pdf/renderer";
import fontRegular from "./fonts/NotoSansTamil-Regular.ttf";
import fontBold from "./fonts/NotoSansTamil-Bold.ttf";

// Register fonts
Font.register({
  family: "NotoSansTamil",
  src: fontRegular,
});
Font.register({
  family: "NotoSansTamilBold",
  src: fontBold,
});

const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontSize: 9,
    fontFamily: "NotoSansTamil",
    backgroundColor: "#ffffff",
  },
  headerContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-end",
    marginBottom: 20,
    borderBottomWidth: 2,
    borderBottomColor: "#1e3a8a",
    paddingBottom: 10,
  },
  titleContainer: {
    flexDirection: "column",
  },
  companyName: {
    fontSize: 22,
    fontFamily: "NotoSansTamilBold",
    color: "#1e3a8a",
  },
  reportTitle: {
    fontSize: 12,
    color: "#64748b",
    marginTop: 4,
  },
  dateText: {
    fontSize: 10,
    color: "#475569",
  },
  table: {
    width: "100%",
    borderWidth: 1,
    borderColor: "#e2e8f0",
    borderRadius: 4,
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#f1f5f9",
    borderBottomWidth: 1,
    borderBottomColor: "#cbd5e1",
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#e2e8f0",
  },
  tableRowAlternate: {
    backgroundColor: "#f8fafc",
  },
  tableCellHeader: {
    padding: 8,
    fontSize: 9,
    fontFamily: "NotoSansTamilBold",
    color: "#334155",
    textAlign: "center",
  },
  tableCell: {
    padding: 8,
    fontSize: 8,
    color: "#475569",
    textAlign: "center",
  },
  textLeft: { textAlign: "left" },
  textRight: { textAlign: "right" },
  // Column flex values
  colDate: { flex: 1.2 },
  colReceipt: { flex: 1.0 },
  colName: { flex: 2.0 },
  colPlace: { flex: 1.5 },
  colMobile: { flex: 1.5 },
  colAmount: { flex: 1.5 },
  colRate: { flex: 0.8 },
  colWeight: { flex: 1.0 },
  colTotalAmt: { flex: 1.5 },
  col1Gram: { flex: 1.2 },
  colStatus: { flex: 1.2 },
  footer: {
    position: "absolute",
    bottom: 20,
    left: 30,
    right: 30,
    flexDirection: "row",
    justifyContent: "space-between",
    borderTopWidth: 1,
    borderTopColor: "#e2e8f0",
    paddingTop: 10,
  },
  footerText: {
    fontSize: 8,
    color: "#94a3b8",
  },
  totalsRow: {
    flexDirection: "row",
    backgroundColor: "#e2e8f0",
    borderBottomWidth: 0,
  },
  totalsCell: {
    padding: 8,
    fontSize: 9,
    fontFamily: "NotoSansTamilBold",
    color: "#1e293b",
  }
});

const DashBoardPDF = ({ data = [], aggregateInterestData = {} }) => {
  const formatDate = (d) => {
    const date = new Date(d);
    return `${date.getDate().toString().padStart(2, "0")}/${(
      date.getMonth() + 1
    )
      .toString()
      .padStart(2, "0")}/${date.getFullYear()}`;
  };

  const todayStr = formatDate(new Date());

  // Calculate totals
  let grandTotalAmount = 0;
  let grandTotalTotalAmt = 0;

  const formattedData = data.map(item => {
    const interestIncome = parseFloat(aggregateInterestData[item.receipt_no]?.total_interest_income || 0);
    const originalAmount = parseFloat(item.original_amount || 0);
    const interestPaymentAmt = parseFloat(item.interest_payment_amount || 0);
    const totalWeight = parseFloat(item.total_weight || 1);

    const totalAmt = (originalAmount + interestPaymentAmt + interestIncome);
    const oneGram = (totalAmt / totalWeight);
    const statusText = item.status === "Closed" ? "மீட்கபட்டது" : "நிலுவை";

    grandTotalAmount += originalAmount;
    grandTotalTotalAmt += totalAmt;

    return {
      ...item,
      originalAmount,
      totalAmt,
      oneGram,
      statusText
    };
  });

  return (
    <Document>
      <Page size="A4" orientation="landscape" style={styles.page}>
        <View style={styles.headerContainer} fixed>
          <View style={styles.titleContainer}>
            <Text style={styles.companyName}>Sri Vigneshwara Finance</Text>
            <Text style={styles.reportTitle}>Pawn Details Report (நகை அடகு விவரங்கள்)</Text>
          </View>
          <Text style={styles.dateText}>Generated on: {todayStr}</Text>
        </View>

        <View style={styles.table}>
          <View style={styles.tableHeader} fixed>
            <Text style={[styles.tableCellHeader, styles.colDate]}>தேதி</Text>
            <Text style={[styles.tableCellHeader, styles.colReceipt]}>அடகு எண்</Text>
            <Text style={[styles.tableCellHeader, styles.colName, styles.textLeft]}>பெயர்</Text>
            <Text style={[styles.tableCellHeader, styles.colPlace, styles.textLeft]}>இடம்</Text>
            <Text style={[styles.tableCellHeader, styles.colMobile]}>மொபைல் </Text>
            <Text style={[styles.tableCellHeader, styles.colAmount, styles.textRight]}>அசல் தொகை </Text>
            <Text style={[styles.tableCellHeader, styles.colRate]}>வட்டி %</Text>
            <Text style={[styles.tableCellHeader, styles.colWeight, styles.textRight]}>எடை</Text>
            <Text style={[styles.tableCellHeader, styles.colTotalAmt, styles.textRight]}>மொத்த தொகை </Text>
            <Text style={[styles.tableCellHeader, styles.col1Gram, styles.textRight]}>1 கிராம்</Text>
            <Text style={[styles.tableCellHeader, styles.colStatus]}>Status</Text>
          </View>

          {formattedData.map((item, i) => (
            <View
              style={[
                styles.tableRow,
                i % 2 === 0 ? styles.tableRowAlternate : null,
              ]}
              key={i}
              wrap={false}
            >
              <Text style={[styles.tableCell, styles.colDate]}>{formatDate(item.pawnjewelry_date)}</Text>
              <Text style={[styles.tableCell, styles.colReceipt]}>{item.receipt_no}</Text>
              <Text style={[styles.tableCell, styles.colName, styles.textLeft]}>{item.name}</Text>
              <Text style={[styles.tableCell, styles.colPlace, styles.textLeft]}>{item.place}</Text>
              <Text style={[styles.tableCell, styles.colMobile]}>{item.mobile_number}</Text>
              <Text style={[styles.tableCell, styles.colAmount, styles.textRight]}>{item.originalAmount.toFixed(2)}</Text>
              <Text style={[styles.tableCell, styles.colRate]}>{item.interest_rate}</Text>
              <Text style={[styles.tableCell, styles.colWeight, styles.textRight]}>{item.total_weight}</Text>
              <Text style={[styles.tableCell, styles.colTotalAmt, styles.textRight]}>{item.totalAmt.toFixed(2)}</Text>
              <Text style={[styles.tableCell, styles.col1Gram, styles.textRight]}>{item.oneGram.toFixed(2)}</Text>
              <Text style={[styles.tableCell, styles.colStatus]}>{item.statusText}</Text>
            </View>
          ))}

          {/* Totals Row */}
          <View style={styles.totalsRow} wrap={false}>
            <Text style={[styles.totalsCell, { flex: 7.2, textAlign: 'right' }]}>Grand Total (மொத்த கூட்டுத்தொகை):</Text>
            <Text style={[styles.totalsCell, styles.colAmount, styles.textRight]}>{grandTotalAmount.toFixed(2)}</Text>
            <Text style={[styles.totalsCell, styles.colRate]}></Text>
            <Text style={[styles.totalsCell, styles.colWeight]}></Text>
            <Text style={[styles.totalsCell, styles.colTotalAmt, styles.textRight]}>{grandTotalTotalAmt.toFixed(2)}</Text>
            <Text style={[styles.totalsCell, styles.col1Gram]}></Text>
            <Text style={[styles.totalsCell, styles.colStatus]}></Text>
          </View>
        </View>

        <View style={styles.footer} fixed>
          <Text style={styles.footerText}>Sri Vigneshwara Finance</Text>
          <Text style={styles.footerText} render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`} />
        </View>
      </Page>
    </Document>
  );
};

export default DashBoardPDF;
